import { findLoneLinkEmbeds, pastedVideoEmbed } from 'utils/video-embed'

import { openVideoDialog, type VideoDialogEditor } from './video-dialog'

/** The slice of the TinyMCE editor API used to add video support. TinyMCE loads from a CDN, so there are no
 * bundled typings. */
export interface VideoEditor extends VideoDialogEditor {
	// Handlers receive different event shapes (paste content, key events); typed loosely here.
	on: (name: string, handler: (event: any) => void) => void
	getBody: () => HTMLElement
	selection: {
		select: (node: Element) => void
		getBookmark: (type?: number, normalized?: boolean) => unknown
		moveToBookmark: (bookmark: unknown) => void
	}
	undoManager: { transact: (callback: () => void) => void }
	ui: { registry: Record<string, (name: string, spec: unknown) => void> }
}

interface VideoSetupOptions {
	uploadVideo: (file: File, onProgress: (percent: number) => void) => Promise<string>
	maxSizeMb: number
	/** The upload file input's `accept` value. */
	accept: string
	/** The compact editor (messages) has no video support beyond what the shared setup registers. */
	isCompact: boolean
}

/** Turns every paragraph that is only a video link into its player, keeping the caret where it was. */
function embedTypedLinks(editor: VideoEditor) {
	const found = findLoneLinkEmbeds(editor.getBody())
	if (!found.length) return

	const bookmark = editor.selection.getBookmark(2, true)
	editor.undoManager.transact(() => {
		for (const { paragraph, embed } of found) {
			editor.selection.select(paragraph)
			editor.insertContent(embed)
		}
	})
	editor.selection.moveToBookmark(bookmark)
}

/**
 * Our "Add video" dialog (upload + link) replaces the built-in Insert/Edit Media entry point; the media plugin
 * stays loaded so existing videos and embeds still render and edit in place. In the full editor a video link
 * also becomes a player when pasted, or typed on its own line.
 */
export function setupVideoEditor(editor: VideoEditor, { uploadVideo, maxSizeMb, accept, isCompact }: VideoSetupOptions) {
	const addVideo = () => openVideoDialog(editor, { uploadVideo, maxSizeMb, accept })

	// Full editor only — the compact editor's content is sanitized without iframes.
	if (!isCompact) {
		editor.on('PastePreProcess', (event: { content: string }) => {
			const embed = pastedVideoEmbed(event.content)
			if (embed) event.content = embed
		})
		// A typed link (the autolink plugin leaves it a plain link) becomes a player once its line is finished — on
		// Enter, or when the editor loses focus — so the editor shows what the page will.
		editor.on('keyup', (event: { key?: string }) => {
			if (event.key === 'Enter') embedTypedLinks(editor)
		})
		editor.on('blur', () => embedTypedLinks(editor))
	}

	editor.ui.registry.addButton('video', { icon: 'embed', tooltip: 'Add video', onAction: addVideo })
	editor.ui.registry.addMenuItem('video', { icon: 'embed', text: 'Video...', onAction: addVideo })
}
